/**
 * Sanitises a caller-supplied post-login destination into a path that can be
 * appended to a trusted origin.
 *
 * Appending an arbitrary string to an origin does not keep you on it. In
 * particular `@evil.example.com` appended to `https://app.example.com` parses
 * as userinfo followed by the host `evil.example.com`, so a crafted login link
 * would land a just-authenticated user on an attacker's page. A leading `//`
 * (or `/\`, which several browsers normalise to `//`) is protocol-relative and
 * escapes the origin the same way.
 *
 * Anything that is not a single-slash-rooted path is therefore discarded rather
 * than corrected, and the caller falls back to the origin root.
 *
 * Accepts whatever a query parser hands over, including the array a repeated
 * `?returnTo=` produces: only one destination is meaningful, so the first wins.
 * Taking the array here rather than at each call site is deliberate — the two
 * products' login routes previously differed on this, and one of them silently
 * discarded a legitimate destination as a result.
 */
export const resolveReturnToPath = (returnTo) => {
    // `Array.isArray` narrows `unknown` to `any[]`, so the element would arrive
    // as `any`. Reading through `readonly unknown[]` keeps it `unknown` and the
    // checks below still have to earn the return value.
    const candidate = Array.isArray(returnTo)
        ? returnTo[0]
        : returnTo;
    if (typeof candidate !== "string" || !candidate.startsWith("/")) {
        return "";
    }
    if (candidate.startsWith("//") || candidate.startsWith("/\\")) {
        return "";
    }
    return candidate;
};
//# sourceMappingURL=returnTo.js.map